import React, { useEffect, useState } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from "@mhome/ui";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { activeScopeState } from "../global-state";
import LoadingSpinner from "../common/loading";
import { getDebugInfo } from "../service/automation-service";
import useApiCall from "../common/api-call";

const AutomationDebugView = () => {
  const path = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeScope] = useRecoilState(activeScopeState);
  const [debugThreads, setDebugThreads] = useState([]);
  const [parseResults, setParseResults] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState({
    parseResults: false,
  });
  const { apiCall } = useApiCall();

  const handleAccordionChange = (panel, event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    handleGetDebugInfo(params["id"]);
    return () => {};
  }, [path]);

  const handleGetDebugInfo = async (automationId) => {
    try {
      const resp = await getDebugInfo(apiCall, activeScope, automationId);
      setDebugThreads(resp?.threads || []);
      setParseResults(resp?.parseResults || []);
    } catch (error) {
      console.error("Error fetching debug info:", error);
      setDebugThreads([]);
      setParseResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 56px)",
      }}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="m-4">
            {/* Parse Results Accordion */}
            <Accordion
              expanded={expandedAccordions["parseResults"]}
              onChange={(e, isExpanded) =>
                handleAccordionChange("parseResults", e, isExpanded)
              }
            >
              <AccordionSummary>
                <Typography variant="subtitle1">Parse Results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {parseResults.length === 0 && (
                  <Typography
                    component="span"
                    variant="body1"
                    color="text.primary"
                    style={{ display: "inline" }}
                  >
                    Build Success
                  </Typography>
                )}
                <List>
                  {parseResults.map((error, index) => (
                    <ListItem key={index} alignItems="flex-start">
                      <ListItemText
                        primary={`${error.code}: ${error.description}`}
                        secondary={
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            style={{ display: "inline" }}
                          >
                            {`${error.level} - Line ${error.start.row}:${error.start.col}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Debug Threads Accordions */}
            {debugThreads.map((debugThread, index) => (
              <React.Fragment key={index}>
                {/* Thread Plan Accordion */}
                <Accordion
                  expanded={expandedAccordions[`thread-${index}-plan`]}
                  onChange={(e, isExpanded) =>
                    handleAccordionChange(`thread-${index}-plan`, e, isExpanded)
                  }
                >
                  <AccordionSummary>
                    <Typography variant="subtitle1">
                      Thread {debugThread.threadId} Plan
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {debugThread.plan}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Thread Memory Accordion */}
                <Accordion
                  expanded={expandedAccordions[`thread-${index}-memory`]}
                  onChange={(e, isExpanded) =>
                    handleAccordionChange(
                      `thread-${index}-memory`,
                      e,
                      isExpanded
                    )
                  }
                >
                  <AccordionSummary>
                    <Typography variant="subtitle1">
                      Thread {debugThread.threadId} Memory
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {debugThread.memory}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AutomationDebugView;
